import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import Breadcrumb, { getArticleBreadcrumb } from '@/components/feature/Breadcrumb';
import { ArticleSchema, FAQSchema } from '@/components/feature/SchemaOrg';
import { getArticleBySlug } from '@/mocks/articles';
import { comics, getComicBySlug } from '@/mocks/comics';
import { categoryMap } from '@/mocks/categories';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeFaq, setActiveFaq] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!slug) {
    return null;
  }

  const articleData = getArticleBySlug(slug);
  const comicData = getComicBySlug(slug);

  if (!articleData || !comicData) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20">
          <div className="max-w-3xl mx-auto px-4 md:px-6 text-center py-20">
            <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
            <p className="text-foreground-500 mb-6">抱歉，您查找的文章不存在。</p>
            <Link
              to="/"
              className="inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const catName = categoryMap[comicData.category] || comicData.category;
  const breadcrumbItems = getArticleBreadcrumb(slug, articleData.title, comicData.category);

  const seoTitle = `${articleData.title}_免费韩漫在线漫画_无删减下拉式韩漫免费阅读-韩漫漫画网站`;
  const seoDesc = `${articleData.summary.substring(0, 140)} - 韩漫漫画网站为您提供${articleData.title}的详细介绍、评分和推荐理由，支持免费韩漫在线漫画阅读。`;
  const seoKeywords = `${articleData.title},免费韩漫,在线漫画,${catName}韩漫,无删减下拉式,韩漫免费阅读`;

  return (
    <>
      <SEOHead
        title={seoTitle}
        description={seoDesc}
        keywords={seoKeywords}
        canonicalUrl={`/article/${slug}`}
        ogType="article"
        ogImage={articleData.coverImage}
        articlePublishedTime="2025-06-15T08:00:00+09:00"
        articleModifiedTime="2026-08-01T08:00:00+09:00"
      />
      <ArticleSchema
        title={seoTitle}
        description={seoDesc}
        url={`/article/${slug}`}
        imageUrl={articleData.coverImage}
        datePublished="2025-06-15T08:00:00+09:00"
        dateModified="2026-08-01T08:00:00+09:00"
        authorName={articleData.author.split('/')[0].trim()}
      />
      <FAQSchema faqs={articleData.faqs} />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />

            {/* Article Header */}
            <article className="py-6">
              <div className="flex flex-col lg:flex-row gap-8 mb-10">
                {/* Cover Image */}
                <div className="flex-shrink-0 w-full lg:w-[280px]">
                  <div className="aspect-[3/4] rounded-lg overflow-hidden bg-background-100">
                    <img
                      src={articleData.coverImage}
                      alt={`${articleData.title} - 免费韩漫在线漫画`}
                      title={`${articleData.title} - 无删减下拉式韩漫免费阅读`}
                      className="w-full h-full object-cover object-top"
                      width={600}
                      height={800}
                    />
                  </div>
                </div>

                {/* Info */}
                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-3">
                    {articleData.title}
                  </h1>
                  <div className="flex items-center flex-wrap gap-3 mb-4">
                    <span className="text-sm text-foreground-500">
                      作者：<strong>{articleData.author}</strong>
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className="flex items-center gap-1 text-sm">
                      <i className="ri-star-fill text-amber-500"></i>
                      <strong className="text-foreground-900">{articleData.rating}</strong>
                      <span className="text-foreground-500">({(articleData.ratingCount / 10000).toFixed(1)}万评)</span>
                    </span>
                    <span className="w-1 h-1 rounded-full bg-foreground-300"></span>
                    <span className={`text-xs px-2 py-0.5 rounded-md font-medium ${
                      articleData.status === '连载中'
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-secondary-100 text-secondary-700'
                    }`}>
                      {articleData.status}
                    </span>
                  </div>

                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {articleData.tags.map((tag) => (
                      <span
                        key={tag}
                        className="text-xs text-primary-700 bg-primary-50 px-2 py-1 rounded-md"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="text-sm text-foreground-700 leading-relaxed mb-4 bg-background-100 rounded-lg p-4">
                    <strong>{articleData.summary}</strong>
                  </div>

                  <Link
                    to="/category/love"
                    className="inline-flex items-center bg-primary-500 hover:bg-primary-600 text-white px-6 py-2.5 rounded-full font-medium text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    浏览更多{catName}类韩漫
                    <i className="ri-arrow-right-line ml-1.5"></i>
                  </Link>
                </div>
              </div>

              {/* Article Content */}
              <div
                className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed
                  prose-h2:text-xl prose-h2:md:text-2xl prose-h2:font-bold prose-h2:font-heading prose-h2:text-foreground-950 prose-h2:mt-10 prose-h2:mb-4
                  prose-h3:text-lg prose-h3:font-semibold prose-h3:text-foreground-900 prose-h3:mt-6 prose-h3:mb-3
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-4
                  prose-strong:text-foreground-900
                  [&_p]:text-sm [&_p]:md:text-base"
                dangerouslySetInnerHTML={{ __html: articleData.content }}
              ></div>

              {/* FAQ */}
              <div className="mt-12 mb-8">
                <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 mb-6">
                  常见问题 - {articleData.title}
                </h2>
                <div className="space-y-3">
                  {articleData.faqs.map((faq, index) => (
                    <div
                      key={index}
                      className="bg-background-100 rounded-lg border border-background-200/70 overflow-hidden"
                    >
                      <button
                        onClick={() => setActiveFaq(activeFaq === index ? null : index)}
                        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer text-sm font-medium text-foreground-800 hover:text-primary-700 transition-colors"
                      >
                        {faq.question}
                        <i
                          className={`ri-arrow-down-s-line text-foreground-400 transition-transform duration-200 ${
                            activeFaq === index ? 'rotate-180' : ''
                          }`}
                        ></i>
                      </button>
                      {activeFaq === index && (
                        <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                          {faq.answer}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Related Comics */}
              <div className="border-t border-background-200/70 pt-10 mb-10">
                <h2 className="text-xl font-bold font-heading text-foreground-950 mb-6">
                  同类推荐
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  {comics
                    .filter((c) => c.category === comicData.category && c.slug !== slug)
                    .slice(0, 4)
                    .map((comic) => (
                      <Link
                        key={comic.slug}
                        to={`/article/${comic.slug}`}
                        className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-300 transition-all duration-300 cursor-pointer"
                      >
                        <div className="aspect-[3/4] overflow-hidden">
                          <img
                            src={comic.coverImage}
                            alt={comic.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                            width={600}
                            height={800}
                          />
                        </div>
                        <div className="p-3">
                          <h3 className="text-sm font-semibold text-foreground-900 line-clamp-1 group-hover:text-primary-600 transition-colors">
                            {comic.title}
                          </h3>
                          <div className="flex items-center gap-1 mt-1 text-xs text-foreground-500">
                            <i className="ri-star-fill text-amber-400 text-[10px]"></i>
                            <span>{comic.rating}</span>
                          </div>
                        </div>
                      </Link>
                    ))}
                </div>
              </div>
            </article>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}